import get from "lodash/get";
import set from "lodash/set";
import find from "lodash/find";
import pick from "lodash/pick";
import uniq from "lodash/uniq";
import sortBy from "lodash/sortBy";
import pullAt from "lodash/pullAt";
import isEmpty from "lodash/isEmpty";
import findIndex from "lodash/findIndex";
import cloneDeep from "lodash/cloneDeep";
import resetForm from "../utils/reset-form";
import { PARAMETER_OPTIONS, PARAMETER_OPTIONS_ARRAY } from "../constants";

const { FREEFORM } = PARAMETER_OPTIONS;
import {
  filterAthletesAttrs,
  filterTasks,
  formatProfileItem,
  whoIsAthleteCoach,
  syncAthleteData,
} from "../utils/user-helpers";
import { buildLibraryArray, sortLibraryArray } from "../utils/exercise-catalog";

const cleanAndSortParameters = (parameters) => {
  return PARAMETER_OPTIONS_ARRAY.filter((elem) => parameters.includes(elem));
};

const otherExerciseProperties = async (
  body,
  files,
  api,
  assert,
  user,
  exercise = null
) => {
  const {
    parameter0,
    parameter1,
    parameter2,
    parameter3,
    exerciseTypeField,
    primaryMuscleGroup,
    secondaryMuscleGroup,
    instructions,
    videoUrl,
    videoDeleteOtherExercise,
  } = body;

  const parameters = cleanAndSortParameters([
    parameter0,
    parameter1,
    parameter2,
    parameter3,
  ]);

  let videoData;
  if (!videoUrl && !isEmpty(files)) {
    videoData = await (
      await api("other-exercise-video", {
        method: "POST",
        body: { userId: user._id, userEmail: user.email },
        files,
      })
    ).json();
  } else if (videoUrl || (exercise && exercise.video && exercise.video.url)) {
    videoData = {
      url: videoUrl || (exercise.video && exercise.video.url),
    };
  }

  return {
    parameters: parameters[0] === FREEFORM ? [parameters[0]] : parameters,
    video:
      videoData ||
      (videoDeleteOtherExercise ? "" : (exercise && exercise.video) || ""), // video empty it means deleted
    exerciseTypeField: exerciseTypeField === "None" ? "" : exerciseTypeField,
    primaryMuscleGroup: primaryMuscleGroup === "None" ? "" : primaryMuscleGroup,
    secondaryMuscleGroup:
      secondaryMuscleGroup === "None" ? "" : secondaryMuscleGroup,
    instructions: instructions ? instructions.trim() : "",
  };
};

const User = (app) => {
  app
    // Add an exercise to the users library.
    .post(
      "/user/add-secondary",
      async ({ req, res, api, locals, assert, load, flash, session }) => {
        const { body, files } = req;

        const { activeUser, query, activeUserToRequest } = locals;
        const { forAthlete } = query;

        if (forAthlete) {
          await load("profileUser", { user: forAthlete });
        }

        const user = locals.profileUser || activeUserToRequest;

        let library = get(user, `exerciseLibrary.${body.primary}`, []);

        assert(body.movement, 400, "You must specify a name for the exercise");
        if (body.primary !== "assistant")
          assert(
            body.repMaximum,
            400,
            "You must specify a Rep Maximum for the exercise"
          );

        const exists = find(library, { movement: body.movement });

        assert(!exists, 400, "This exercise is already in the library");
        let otherExtraProperties = await otherExerciseProperties(
          body,
          files,
          api,
          assert,
          user
        );

        library.push(
          Object.assign(
            {
              movement: body.movement,
              repMaximum: body.repMaximum || null,
              customizedRM: true,
            },
            otherExtraProperties
          )
        );

        library = sortBy(library, [
          (secondary) => String(secondary.movement).toLowerCase(),
        ]);

        flash("success", "Successfully added");

        set(user, `exerciseLibrary.${body.primary}`, library);

        await api(`user/${user._id}`, {
          method: "PUT",
          body: {
            ...pick(user, "exerciseLibrary"),
            activeUserToRequest,
          },
        });

        //commented this out teporarily; to ensure Cole is aware
        const refreshOpts = {};
        if (body.source === "settings") refreshOpts.modal = "";
        else refreshOpts.secondaryAdded = true;
        res.refreshWith(refreshOpts, { url: "back", hash: "#_" });
      }
    )

    .post(
      "/user/edit-secondary/:index",
      async ({ req, res, api, locals, assert, load, flash }) => {
        const { params, body, files } = req;
        const { query, activeUserToRequest } = locals;
        const { forAthlete } = query;
        if (forAthlete) {
          await load("profileUser", { user: forAthlete });
        }
        const user = locals.profileUser || activeUserToRequest;
        // Sorting the library before editing was causing the wrong movement to be picked
        // on unsorted libraries (e.g. brand new accounts)
        let library = cloneDeep(
          get(user, `exerciseLibrary.${body.primary}`, [])
        );

        assert(
          library[params.index],
          400,
          "This custom movement has been deleted."
        );
        assert(body.movement, 400, "You must specify a name for the exercise.");
        if (body.primary !== "assistant")
          assert(
            body.repMaximum,
            400,
            "You must specify a Rep Maximum for the exercise."
          );

        let otherExtraProperties = await otherExerciseProperties(
          body,
          files,
          api,
          assert,
          user,
          library[params.index]
        );

        const movementBeingUpdated = { ...library[params.index] };

        library[params.index] = {
          movement: body.movement,
          repMaximum: body.repMaximum || null,
          customizedRM: true,
          ...otherExtraProperties,
        };

        const exists = library.filter(
          (item) => item.movement === body.movement
        );

        assert(
          exists?.length <= 1,
          400,
          "An exercise with this name already exists."
        );

        library = sortBy(library, [
          (secondary) => String(secondary.movement).toLowerCase(),
        ]);
        set(user, `exerciseLibrary.${body.primary}`, library);

        const exerciseLibrary = pick(user, "exerciseLibrary").exerciseLibrary;

        let newBody = { exerciseLibrary, body };
        //if before there was a video link and in the body the link has been deleted
        if (library[params.index].video?.url && body.videoUrl?.length === 0) {
          newBody.exerciseLibrary[body.primary][params.index].video.url = "";
        }
        if (body.primary !== "assistant") {
          if (
            movementBeingUpdated.repMaximum != body.repMaximum &&
            confirm("Apply new 1 rep max to all upcoming workouts?")
          ) {
            newBody.action = "updateFutureRM";
            newBody.type = "secondary";
            newBody.primary = body.primary;
            newBody.secondary = body.movement;
            newBody.secondaryRepMaximum = body.repMaximum;
          }
        }

        await api(`user/${user._id}`, {
          method: "PUT",
          body: newBody,
        });

        api(`update-cycle-exercises-metadata`, {
          method: "PUT",
          body: {
            email: user.email,
            primary: body.primary,
            movement: body.movement,
            prevMovement: movementBeingUpdated.movement,
          },
        });

        flash("success", "Successfully updated");
        res.refreshWith({ modal: "" }, { url: "back" });
      }
    )
    .get(
      "/user/remove-secondary/:primary/:index",
      async ({ req, res, api, locals, assert, load, session, flash }) => {
        // if (
        //   process.browser &&
        //   !confirm("Are you sure you want to delete this exercise?")
        // ) {
        //   return res.backDontScroll();
        // }

        const { params, query } = req;
        const { activeUser, activeUserToRequest } = locals;
        const { forAthlete } = query;

        if (forAthlete) {
          await load("profileUser", { user: forAthlete });
        }
        const user = locals.profileUser || activeUserToRequest;

        const library = uniq(
          get(user, `exerciseLibrary.${params.primary}`) || []
        );
        pullAt(library, params.index);

        set(
          user,
          `exerciseLibrary.${params.primary}`,
          isEmpty(library) ? [] : library
        );

        /**
         * If the exercise deleted was the last one of the library we are going to delete that property
         * from the exercise library
         */
        if (user.exerciseLibrary && typeof user.exerciseLibrary === "object") {
          Object.keys(user.exerciseLibrary).forEach((key) => {
            if (
              Array.isArray(user.exerciseLibrary[key]) &&
              isEmpty(user.exerciseLibrary[key])
            ) {
              delete user.exerciseLibrary[key];
            }
          });
        }

        try {
          await api(`user/${user._id}`, {
            method: "PUT",
            body: pick(user, "exerciseLibrary"),
          });
          flash("success", "Successfully deleted");
        } catch (error) {
          flash("error", e.message);
        }

        res.backDontScroll();
      }
    )
    .get(
      "/user/update-email/:token",
      async ({ req: { params }, res, api, flash }) => {
        await (
          await api("update-email", {
            method: "POST",
            body: { token: params.token },
          })
        ).json();
        flash("success", "Successfully changed");
        res.redirect("/dashboard");
      }
    )
    .post(
      "/user/reset-email",
      async ({ req, locals, res, api, flash, assert }) => {
        const { body } = req;

        assert(body.email, 400, "A new email address is required");
        assert(
          String(body.email).toLowerCase() !== locals.activeUser.email,
          400,
          "You must provide a different email than your existing email"
        );
        assert(body.currentPassword, 400, "Your current password is required");

        await api("change-email", { method: "POST", body });
        flash("success", "An email has been sent to verify your new email");
        res.backDontScroll();
      }
    )
    .post(
      "/user/change-password",
      async ({ req, locals, res, api, flash, assert }) => {
        try {
          const { body } = req;

          assert(body.currentPassword, 400, "Current password is required");
          assert(body.password, 400, "A new password is required");
          assert(
            body.confirmPassword,
            400,
            "A new password confirmation is required"
          );
          assert(
            body.confirmPassword === body.password,
            400,
            "New passwords must match"
          );

          await api(`user-change-password`, { method: "PUT", body }).then(
            (response) => {
              if (response.status === 200) {
                flash("success", "Successfully updated");
                resetForm("#form-change-password");
                res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
              } else {
                flash(
                  "error",
                  res.statusText ||
                    "An error occurred while updating your password"
                );
                res.refreshWith({}, { url: "back", hash: "#_" });
              }
            }
          );
        } catch (e) {
          flash("error", e.message);
          res.refreshWith({}, { url: "back", hash: "#_" });
        }
      }
    )
    .post(
      "/user/update-password",
      async ({ req: { body }, res, api, login, flash }) => {
        const { token, userId } = await (
          await api("update-password", {
            method: "POST",
            body,
          })
        ).json();
        login({ _id: userId, token });
        res.cookie("loggedin", "1");
        flash("success", "Your password has been successfully updated");
        res.redirect("/dashboard");
      }
    )

    .post(
      "/user/reset-password",
      async ({ req: { body }, res, api, flash }) => {
        await api("change-password", { method: "POST", body });
        flash(
          "success",
          "A password reset message has been sent to your inbox"
        );
        res.backDontScroll();
      }
    )
    .post(
      "/user/update-notifications",
      async ({ req: { body }, res, locals, api, flash }) => {
        const { activeUser } = locals;
        if (isEmpty(body.notifications)) body.notifications = null;
        body.action = "settings";
        await api(`user/${activeUser._id}`, { method: "PUT", body });
        flash("success", "Successfully updated");
        res.backDontScroll();
      }
    )
    .post(
      "/user/user-denied-signup",
      async ({ req: { body }, res, assert, locals, api, flash }) => {
        const { activeUser } = locals;
        body.user = activeUser;

        assert(body.whyNotSignup, 400, "Please select at least one checkbox");

        await api(`feedback`, { method: "POST", body });
        flash("success", "Your feedback has been received");
        res.redirect("/logout");
      }
    )
    .post(
      "/user/cancel-account",
      async ({ req, res, locals, api, fail, flash, assert, logout }) => {
        assert(req.body.password, 400, "You must enter your password");
        try {
          await api(`user-cancel-subscription`, {
            method: "PUT",
            body: req.body,
          });
          flash(
            "success",
            "Your subscription has successfully been cancelled.\nYou will no longer be able to log in once your current subscription has expired"
          );
          res.redirect("/dashboard/settings/subscription");
        } catch (e) {
          flash("error", e.message);
          const modal = get(req.query, "modal", {});
          res.redirect(
            "/dashboard/settings/subscription?modal%5Btype%5D=cancel"
          );
        }
      }
    )
    .post(
      "/user/update-permission-plan",
      async ({
        req: { body },
        res,
        locals,
        api,
        fail,
        flash,
        assert,
        logout,
        session,
      }) => {
        try {
          assert(
            body.userId,
            400,
            "You must specify the user, whose permission you are changing"
          );
          if (!body.plan) {
            body.plan = null;

            if (
              body.askForCreditCard === "false" &&
              body.permissions.includes("coach")
            ) {
              await api("register-coach", {
                method: "POST",
                body: { existingEmail: body.email },
              });
            }
          }
          delete body.email;
          await api("update-permission-plan", { method: "PUT", body });

          flash("success", "Permission updated");
          res.refreshWith({ modal: "", user: "" }, { url: "back", hash: "#_" });
        } catch (err) {
          flash("error", err.message);
        }
      }
    )
    .post(
      "/user/athlete-video-shown",
      async ({ req: { body }, res, api, flash }) => {
        try {
          await api("athlete-video-shown", { method: "PUT", body: {} });
        } catch (err) {
          flash("error", err.message);
        }
      }
    )
    .post("/user/video-shown", async ({ req: { body }, res, api, flash }) => {
      try {
        await api("video-shown", {
          method: "PUT",
          body,
        });
      } catch (err) {
        flash("error", err.message);
      }
    })
    // A coach stops training an athlete
    .post(
      "/user/stop-training/:athleteId",
      async ({ req: { body, params }, res, locals, api, flash, assert }) => {
        assert(params.athleteId, 400, "You must specify an athlete to update");
        const { activeUser, organizationRoleName } = locals;
        const { athleteId } = params;

        try {
          // Nutritionist can't stop training athletes
          assert(
            organizationRoleName !== "Nutritionist",
            400,
            "You don't have permission to perform this action"
          );

          // Check if user acknowledged all the points
          assert(
            body.acknowledge && body.acknowledge.length === 5,
            400,
            "Please acknowledge that you understand all points"
          );

          const userToRequest =
            !!body.coachOrganizationInformation &&
            String(
              whoIsAthleteCoach(
                activeUser.athletes,
                athleteId,
                activeUser._id,
                JSON.parse(body.coachOrganizationInformation)._id
              )
            ) !== String(activeUser._id)
              ? JSON.parse(body.coachOrganizationInformation)
              : activeUser;

          delete body.coachOrganizationInformation;

          // Find athlete to change
          const athleteIndex = findIndex(userToRequest.athletes, {
            _id: athleteId,
          });
          let athleteToChange = find(userToRequest.athletes, {
            _id: athleteId,
          });

          assert(
            !athleteToChange.isGymMember,
            400,
            "Cannot stop training a PRESS user"
          );

          // Update athlete in coach's athletes array
          athleteToChange.status = {
            inactive: true,
            inactiveReason: "Coach stopped training",
          };

          userToRequest.athletes[athleteIndex] = athleteToChange;
          const athletes = userToRequest.athletes.map((athlete) =>
            filterAthletesAttrs(athlete)
          );

          // Prepare API requests
          // Update the athlete user (delete the coach)
          const updateAthleteRequest = api(`user/${athleteId}`, {
            method: "PUT",
            body: {
              coach: { dummy: true },
              proxyAthlete: athleteId,
            },
          });
          // Update the coach user
          const updateCoachAthletesRequest = api(`user-update-coach-athletes`, {
            method: "PUT",
            body: {
              athletes,
              action: "updateCoachAthletes",
              coachId: userToRequest._id,
            },
          });

          const stopBillingRequest = api("stop-billing-coach-for-athlete", {
            method: "PUT",
            query: {
              athlete: athleteId,
              userBillingId: locals.activeUserToRequest._id,
              coachId: userToRequest._id,
            },
          });

          // Execute all requests concurrently
          await Promise.all([
            updateAthleteRequest,
            updateCoachAthletesRequest,
            stopBillingRequest,
          ]);

          flash("success", `You have stopped training ${athleteToChange.name}`);
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        } catch (error) {
          flash("error", `An error occured trying to stop training this user`);
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        }
      }
    )
    // A coach deletes an athlete from their list of athletes
    .post(
      "/user/remove-athlete/:athleteId",
      async ({
        req: { body, params },
        res,
        locals,
        api,
        fail,
        flash,
        assert,
        logout,
        session,
      }) => {
        assert(params.athleteId, 400, "You must specify an athlete to update");
        const { activeUser, activeUserToRequest, organizationRoleName } =
          locals;
        const { athleteId } = params;
        const referAsClients = Boolean(activeUser.referAsClients);

        // Nutritionist can't stop training athletes
        assert(
          organizationRoleName !== "Nutritionist",
          400,
          "You don't have permission to perform this action"
        );

        // Check if user acknowledged all points
        assert(
          body.acknowledge.length === 3,
          400,
          "Please acknowledge that you understand all points"
        );

        const userToRequest =
          !!body.coachOrganizationInformation &&
          String(
            whoIsAthleteCoach(
              activeUser.athletes,
              athleteId,
              activeUser._id,
              JSON.parse(body.coachOrganizationInformation)._id
            )
          ) !== String(activeUser._id)
            ? JSON.parse(body.coachOrganizationInformation)
            : activeUser;

        delete body.coachOrganizationInformation;

        // Find athlete to delete
        const athleteIndex = findIndex(userToRequest.athletes, {
          _id: athleteId,
        });
        let athleteToChange = find(userToRequest.athletes, { _id: athleteId });

        assert(!athleteToChange.isGymMember, 400, "Cannot remove a PRESS user");

        // pick only needed fields
        userToRequest.athletes = userToRequest.athletes.map((athlete) =>
          filterAthletesAttrs(athlete)
        );
        // Update coach
        // There is a problem where empty fields get dropped somewhere in the
        // request, so that api thinks there's nothing to update. Have to pass dummy data

        userToRequest.athletes.splice(athleteIndex, 1);
        if (userToRequest.athletes.length === 0)
          userToRequest.athletes = { dummy: true };
        await api(`user-update-coach-athletes`, {
          method: "PUT",
          body: {
            athletes: userToRequest.athletes,
            action: "updateCoachAthletes",
            coachId: userToRequest._id,
          },
        });

        // Update Athlete
        const athlete = JSON.parse(body.athlete);
        if (!athlete.avatar) delete athlete.avatar;
        athlete.coach = { dummy: true };
        athlete.proxyAthlete = athleteId;
        await api(`user/${athleteId}`, {
          method: "PUT",
          body: athlete,
        });

        flash(
          "success",
          `You have removed ${athleteToChange.name} from your ${
            referAsClients ? "clients" : "athletes"
          }`
        );
        res.redirect("/coaching-portal/");
      }
    )
    // A coach updates the profile they have on an athlete
    .post("/user/update-athlete-profile/:athleteId", async (attribute) => {
      const {
        req: { body, params },
        res,
        locals,
        api,
        flash,
        assert,
        load,
      } = attribute;

      assert(params.athleteId, 400, "You must specify a user to update");
      const { activeUser } = locals;

      const athleteId = params.athleteId;
      await load("athlete", { user: athleteId });

      const userToRequest =
        !!body.coachOrganizationInformation &&
        String(
          whoIsAthleteCoach(
            activeUser.athletes,
            athleteId,
            activeUser._id,
            body.coachOrganizationInformation._id
          )
        ) !== String(activeUser._id)
          ? body.coachOrganizationInformation
          : activeUser;

      delete body.coachOrganizationInformation;

      const athleteToChange = find(userToRequest.athletes, {
        _id: athleteId,
      });
      // Update athlete
      const booleanGoals = body.goals ? formatProfileItem(body.goals) : [];
      const booleanLimitations = body.limitations
        ? formatProfileItem(body.limitations)
        : [];
      const booleanEquipment = body.equipment
        ? formatProfileItem(body.equipment)
        : [];

      athleteToChange.goals = booleanGoals;
      athleteToChange.limitations = booleanLimitations;
      athleteToChange.equipment = booleanEquipment;
      athleteToChange.notes =
        body.notes !== undefined ? body.notes : athleteToChange.notes;
      syncAthleteData(athleteToChange, locals.athlete);

      //Save and refresh
      await api(`update-user-athlete/${userToRequest._id}`, {
        method: "PUT",
        body: filterAthletesAttrs(athleteToChange),
      });

      flash("success", "Successfully updated");
      res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
    })
    .post("/user/generate-account-link", async ({ res, api }) => {
      const link = await (
        await api(`generate-account-links`, {
          method: "POST",
        })
      ).json();
      res.refreshWith(
        { link: link.accountLinks.url },
        { url: "back", hash: "#_" }
      );
    })
    .post(
      "/user/update-video-upload-permission",
      async ({ req: { body }, res, api, flash }) => {
        await api(`user/${body.userId}`, {
          method: "PUT",
          body: {
            videoUploadForbidden: Boolean(Number(body.videoUploadForbidden)),
          },
        });
        flash("success", "Permission updated");
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/user/update-custom-mobile-icon-permission",
      async ({ req: { body }, res, api, flash }) => {
        await api(`user/${body.userId}`, {
          method: "PUT",
          body: {
            customMobileIcon: Boolean(Number(body.customMobileIcon)),
          },
        });
        flash("success", "Custom mobile icon permission updated");
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/user/update-custom-appstore-listing-permission",
      async ({ req: { body }, res, api, flash }) => {
        await api(`user/${body.userId}`, {
          method: "PUT",
          body: {
            appStoreListingEnable: Boolean(Number(body.appStoreListingEnable)),
          },
        });
        flash("success", "Custom AppStore listing permission updated");
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/user/update-referral-program",
      async ({ req: { body }, res, api, flash }) => {
        const { purpose } = body;
        const message =
          purpose === "hide-link"
            ? "Referral link program container will be permanently hidden"
            : "The referral link have been updated successfully";
        delete body.purpose;

        await api(`referral-link-info`, {
          method: "PUT",
          body,
        });
        flash("success", message);
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post("/user/enable-chat", async ({ req: { body }, res, api, flash }) => {
      await api(`user/${body.userId}`, {
        method: "PUT",
        body: {
          chatEnabled: true,
        },
      });
      flash("success", "Chat enabled successfully");
      res.refreshWith(
        { modal: "" },
        { url: "/coaching-portal/chat", hash: "#_" }
      );
    })
    // Add exercises from the catalog to the users library.
    .post(
      "/user/catalog-add-exercises",
      async ({ req, res, api, locals, assert, flash }) => {
        const { activeUserToRequest, activeUser } = locals;
        const { selectedExercises, onboarding = false } = req.body;
        // defaulting to empty arrays because rill is making these undefined if the component sends empty arrays
        try {
          if (onboarding && activeUser?._id !== activeUserToRequest?._id) {
            assert(
              false,
              400,
              "You don't have permission to perform this action"
            );
          }
          const {
            squat = [],
            bench = [],
            deadlift = [],
            assistant = [],
            clean = [],
            jerk = [],
            snatch = [],
            overheadPress = [],
            other = [],
          } = selectedExercises;

          assert(
            squat.length ||
              bench.length ||
              deadlift.length ||
              assistant.length ||
              clean.length ||
              jerk.length ||
              snatch.length ||
              overheadPress.length ||
              other.length,
            400,
            "You must select at least one exercise to add"
          );

          const emptyLibrary = {
            squat: [],
            bench: [],
            deadlift: [],
            assistant: [],
            clean: [],
            jerk: [],
            snatch: [],
            overheadPress: [],
            other: [],
          };
          // make a new library if onboarding
          const library = {
            ...(onboarding
              ? emptyLibrary
              : activeUserToRequest.exerciseLibrary),
          };

          const squatArr = buildLibraryArray(squat, 100);
          const benchArr = buildLibraryArray(bench, 100);
          const deadliftArr = buildLibraryArray(deadlift, 100);
          const assistantArr = buildLibraryArray(assistant);
          const cleanArr = buildLibraryArray(clean, 100);
          const jerkArr = buildLibraryArray(jerk, 100);
          const snatchArr = buildLibraryArray(snatch, 100);
          const overheadPressArr = buildLibraryArray(overheadPress, 100);
          const otherArr = buildLibraryArray(other, 100);

          if (squatArr.length) {
            library.squat = sortLibraryArray(library.squat.concat(squatArr));
          }
          if (benchArr.length) {
            library.bench = sortLibraryArray(library.bench.concat(benchArr));
          }
          if (deadliftArr.length) {
            library.deadlift = sortLibraryArray(
              library.deadlift.concat(deadliftArr)
            );
          }
          if (assistantArr.length) {
            library.assistant = sortLibraryArray(
              library.assistant.concat(assistantArr)
            );
          }
          if (cleanArr.length) {
            library.clean = sortLibraryArray(library.clean.concat(cleanArr));
          }
          if (jerkArr.length) {
            library.jerk = sortLibraryArray(library.jerk.concat(jerkArr));
          }
          if (snatchArr.length) {
            library.snatch = sortLibraryArray(library.snatch.concat(snatchArr));
          }
          if (overheadPressArr.length) {
            library.overheadPress = sortLibraryArray(
              library.overheadPress.concat(overheadPressArr)
            );
          }
          if (otherArr.length) {
            library.other = sortLibraryArray(library.other.concat(otherArr));
          }

          await api(`user/${activeUserToRequest._id}`, {
            method: "PUT",
            body: { exerciseLibrary: library },
          });

          set(activeUser, "exerciseLibrary", library);
          set(activeUserToRequest, "exerciseLibrary", library);

          if (onboarding) {
            flash("success", "Account setup successful");
            return res.redirect("/coaching-portal");
          }

          flash("success", "Exercises added successfully to your library");

          res.redirect("/coaching-portal/exercise-library");
        } catch (error) {
          if (onboarding && activeUser?._id !== activeUserToRequest?._id) {
            console.error("Error: ", error?.message);
            console.error(
              "Trying to re-signup a coach part of an organization"
            );
            res.refreshWith(
              {
                flashError:
                  "Trying to re-signup a coach part of an organization",
              },
              { url: "/logout", hash: "#_" }
            );
          } else {
            console.error("Error setting up account", error?.message);
          }
        }
      }
    )

    .post(
      "/user/check-chat-users/:athleteId/:fromCommunity",
      async ({ req: { params }, res, api, flash }) => {
        const { fromCommunity = "false", athleteId = null } = params;
        const response = await (
          await api(
            `check-conversation?id=${params.athleteId}&fromCommunity=${fromCommunity}`
          )
        ).json();
        const { conversation, chatUserExist } = response;
        if (chatUserExist) {
          const url =
            fromCommunity === "true"
              ? `/coaching-portal/community-chat?initId=${conversation}`
              : `/coaching-portal/chat?initId=${athleteId}`;
          res.redirect(url);
        } else {
          flash(
            "error",
            "An error occurred while the user was being checked. Please try again later or contact support."
          );
          res.backDontScroll();
        }
      }
    )

    .post(
      "/user/press-custom-branding/gym-location",
      async ({ req, res, flash, api, locals }) => {
        const { body } = req;
        const { bodyformatting = [] } = body;
        const { activeUser } = locals;
        const { pressCustomBranding = {} } = activeUser;

        const newBody = {};

        newBody.pressCustomBranding = Object.assign({}, pressCustomBranding);
        newBody.pressCustomBranding.gymLocations = bodyformatting;

        try {
          await api(`user/${activeUser._id}`, {
            method: "PUT",
            body: newBody,
          });
          flash("success", "Gym location deleted successfully.");
        } catch (e) {
          flash("error", e.message);
        }

        res.redirect("/coaching-portal/settings/gym-locations");
      }
    )
    .post(
      "/user/update-athlete-tasks/:athleteId",
      async ({ req: { body, params }, res, api, flash, locals, assert }) => {
        assert(params.athleteId, 400, "You must specify a user to update");
        const { activeUser } = locals;
        const athleteId = params.athleteId;

        const userToRequest =
          !!body.coachOrganizationInformation &&
          String(
            whoIsAthleteCoach(
              activeUser.athletes,
              athleteId,
              activeUser._id,
              body.coachOrganizationInformation._id
            )
          ) !== String(activeUser._id)
            ? body.coachOrganizationInformation
            : activeUser;
        delete body.coachOrganizationInformation;

        const athlete = userToRequest.athletes.find(
          (athlete) => athlete._id === athleteId
        );
        athlete.tasks = body;

        if (Object.keys(athlete.tasks).includes("undefined")) {
          athlete.tasks = filterTasks(athlete.tasks);
        }

        await api(`update-user-athlete/${userToRequest._id}`, {
          method: "PUT",
          body: athlete,
        });
        flash("success", "Successfully updated");

        res.backDontScroll();
      }
    )
    .post(
      "/user/cancel-subscriber",
      async ({ api, assert, flash, req, res, locals }) => {
        const { body } = req;
        assert(body.customer, 400, "Customer ID is required");
        assert(
          body.type === "membership" || body.type === "subscription",
          400,
          "Product type of membership or subscription is required"
        );
        let bodyToSend = body;
        if (locals.activeUser.organizationId) {
          bodyToSend = {
            ...body,
            organizationUser: locals.activeUserToRequest._id,
          };
        }
        try {
          await api("cancel-subscriber", {
            method: "PUT",
            body: bodyToSend,
          });
          flash("success", "Successfully cancelled");
          res.refreshWith({ customer: "" }, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.refreshWith({ customer: "" }, { url: "back", hash: "#_" });
        }
      }
    )
    .post(
      "/user/change-membership",
      async ({ api, assert, flash, req, res }) => {
        const { body } = req;
        assert(body.customer, 400, "Customer ID is required");
        assert(body.price, 400, "Plan to change to is required");
        try {
          await api("manage-press-membership", {
            method: "PUT",
            body: {
              proxyUser: body.customer,
              plan: body.price,
            },
          });
          flash("success", "Successfully changed");
          res.refreshWith(
            { customer: "", price: "" },
            { url: "back", hash: "#_" }
          );
        } catch (e) {
          flash("error", e.message);
          res.refreshWith(
            { customer: "", price: "" },
            { url: "back", hash: "#_" }
          );
        }
      }
    )
    .post(
      "/user/send-custom-email",
      async ({ api, assert, flash, req, res }) => {
        const { body } = req;
        assert(body.type, 400, "type is required");
        try {
          await api("send-custom-email", {
            method: "POST",
            body,
          });
          flash(
            "success",
            "Please check your email for the download links for the Coach Mobile App"
          );
        } catch (e) {
          flash("error", e.message);
        }
        res.backDontScroll();
      }
    )
    .post(
      "/user/enable-community-chat",
      async ({ api, assert, flash, res, locals }) => {
        const { activeUserToRequest } = locals;
        const { _id: userId, pushAccountInfo } = activeUserToRequest;
        assert(userId, 400, "User ID is required");
        assert(pushAccountInfo, 400, "User must be PUSH");

        const updatedPushAccountInfo = {
          ...pushAccountInfo,
          inAppCommunityEnabled: true,
        };

        try {
          await api(`user/${userId}`, {
            method: "PUT",
            body: { pushAccountInfo: updatedPushAccountInfo },
          });
          flash("success", "Community Chat had been successfully enabled");
          res.backDontScroll();
        } catch (e) {
          flash("error", e.message);
          res.backDontScroll();
        }
      }
    )
    .post(
      "/user/facilikey-resync",
      async ({ api, assert, flash, req, res }) => {
        const { body } = req;
        assert(body.customer, 400, "Customer ID is required");
        try {
          await api("facilikey-resync", {
            method: "POST",
            body: {
              proxyUser: body.customer,
            },
          });
          flash("success", "Successfully re-synced");
          res.refreshWith({ customer: "" }, { url: "back", hash: "#_" });
        } catch (e) {
          flash("error", e.message);
          res.refreshWith({ customer: "" }, { url: "back", hash: "#_" });
        }
      }
    )
    .post(
      "/user/edit-meal-plan-data-source-preference",
      async ({ req: { body }, res, api, flash }) => {
        try {
          await api(`meal-plan-data-source-preference`, {
            method: "PUT",
            body,
          });
          flash("success", "User preferences updated successfully");
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    );
};

export default User;
